import { firestore } from "@/firebase/config";
import { isPOINullMetaType, type POI, type POIType } from "@inloco/shared";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";

const LIVE_POI_TYPES: POIType[] = [
    // Configurable list: only these POI types will be kept in realtime on the map
    'danger',
];
export function usePoisWithLiveUpdates(pois: POI[], appSegmentId: string | null) {

    const livePoiIds = useMemo(() => {
        return pois
            .filter(p => LIVE_POI_TYPES.includes(p.type))
            .map(p => p.id);
    }, [pois]);

    const [livePoisById, setLivePoisById] = useState<Record<string, POI>>({});

    // Realtime only for selected POIs on the map
    useEffect(() => {
        if (!appSegmentId || livePoiIds.length === 0) return;

        const unsubs = livePoiIds.map((poiId) => {
            const poiDocRef = doc(firestore, 'apps', appSegmentId, 'pois', poiId);

            return onSnapshot(poiDocRef, (snap) => {
                if (!snap.exists()) {
                    setLivePoisById(prev => {
                        const next = { ...prev };
                        delete next[poiId];
                        return next;
                    });
                    return;
                }

                const raw = snap.data() as any;
                const type = raw.type as POIType;

                const meta = isPOINullMetaType(type) ? null : raw.meta;

                const updated = {
                    ...raw,
                    meta,
                    id: snap.id,
                } as POI;

                setLivePoisById(prev => ({ ...prev, [poiId]: updated }));
            });
        });

        return () => {
            unsubs.forEach(unsub => unsub());
        };
    }, [appSegmentId, livePoiIds]);

    // Per ottimizzare il rendering, creiamo un set degli ID dei POI live
    const livePoiIdSet = useMemo(() => new Set(livePoiIds), [livePoiIds]);

    // Combina POI statici e live, dando priorità ai live quando presenti
    const poisWithLiveUpdates = useMemo(() => {
        return pois.map(p => (livePoiIdSet.has(p.id) ? (livePoisById[p.id] ?? p) : p));
    }, [pois, livePoisById, livePoiIdSet]);

    return poisWithLiveUpdates;
}